
import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import {
  ArrowLeft, MapPin, Gauge, Settings, Fuel, Calendar,
  CheckCircle, Share2, Phone, Mail, Truck as TruckIcon, Info, ArrowRight, MessageCircle, ChevronLeft, ChevronRight
} from 'lucide-react';

import DOMPurify from 'dompurify';
import { sendFormEmail } from '../utils/emailService';

const VehicleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { trucks, settings, addInquiry } = useData();
  const navigate = useNavigate();
  const truck = trucks.find(t => t.id === id);

  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  // State for active image interaction
  const [activeImage, setActiveImage] = useState<string>('');

  // Initialize active image when truck loads
  React.useEffect(() => {
    if (truck) {
      setActiveImage(truck.image || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80&w=1000');
    }
  }, [truck]);

  // Fallback image constant
  const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80&w=1000';

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = FALLBACK_IMAGE;
    e.currentTarget.onerror = null;
  };

  // Combine main image and gallery for navigation
  const allImages = truck ? [truck.image || FALLBACK_IMAGE, ...(truck.gallery || [])] : [];

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = allImages.indexOf(activeImage);
    const nextIndex = (currentIndex + 1) % allImages.length;
    setActiveImage(allImages[nextIndex]);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = allImages.indexOf(activeImage);
    const prevIndex = (currentIndex - 1 + allImages.length) % allImages.length;
    setActiveImage(allImages[prevIndex]);
  };

  if (!truck) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Vehicle Not Found</h2>
        <p className="text-gray-500 mb-8">The vehicle you are looking for may have been removed or sold.</p>
        <Link to="/inventory" className="bg-automax-red text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider hover:bg-red-700 transition-colors">
          Back to Inventory
        </Link>
      </div>
    );
  }

  // Find similar trucks (safe to do here since truck is defined)
  const similarTrucks = trucks
    .filter(t => t.category === truck.category && t.id !== truck.id)
    .slice(0, 3);

  const [isSending, setIsSending] = useState(false);

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    const fullMessage = `${truck.year} ${truck.name}: ${inquiryForm.message}`;
    const formData = {
      name: inquiryForm.name,
      email: inquiryForm.email,
      phone: inquiryForm.phone,
      message: fullMessage,
      type: 'Used Truck Inquiry'
    };

    // STEP 1: Save to Database FIRST (guaranteed)
    addInquiry({
      id: Math.random().toString(36).substr(2, 9),
      ...formData,
      message: fullMessage,
      type: 'Quote',
      status: 'New',
      date: new Date().toISOString(),
      truckId: truck.id
    });

    // STEP 2: Show success IMMEDIATELY
    setSubmitted(true);
    setInquiryForm({ name: '', email: '', phone: '', message: '' });
    setIsSending(false);

    // STEP 3: Try email in background (non-blocking)
    sendFormEmail(formData, 'inquiry');
    setIsSending(false);
  };

  const specs = [
    { label: 'Max Speed', value: truck.maxSpeed, icon: <Gauge size={18} /> },
    { label: 'Transmission', value: truck.transmission || 'Manual', icon: <Settings size={18} /> },
    { label: 'Fuel Type', value: truck.fuelType || 'Diesel', icon: <Fuel size={18} /> },
    { label: 'Engine', value: truck.engine, icon: <Settings size={18} /> },
    { label: 'Horsepower', value: `${truck.horsepower} HP`, icon: <Gauge size={18} /> },
    { label: 'Condition', value: truck.condition || 'Used', icon: <Info size={18} /> },
    { label: 'Color', value: truck.color || 'N/A', icon: <TruckIcon size={18} /> },
  ].filter(spec => {
    // Hide engine-related specs for trailers
    if (truck.category.toLowerCase().includes('trailer')) {
      return !['Max Speed', 'Transmission', 'Fuel Type', 'Engine', 'Horsepower'].includes(spec.label);
    }
    return true;
  });

  // WhatsApp Message Formatting
  const whatsappNumber = settings.phoneNumber.replace(/[^0-9]/g, '');
  const whatsappText = encodeURIComponent(`Hi Automax, I'm interested in the ${truck.year} ${truck.brand} ${truck.name} (Ref: ${truck.id}). Is it still available?`);
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappText}`;

  return (
    <div className="bg-gray-50 min-h-screen pt-20 md:pt-24 pb-12">
      {/* Breadcrumb & Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-500 hover:text-automax-red transition-colors font-medium mb-6 text-sm md:text-base"
        >
          <ArrowLeft size={18} className="mr-2" /> Back to Inventory
        </button>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 bg-white p-6 rounded-sm shadow-sm border border-gray-100">
          <div>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 uppercase leading-tight">{truck.name}</h1>
            <p className="text-gray-500 text-sm md:text-base mt-2 flex flex-wrap items-center gap-2">
              <span className="font-bold text-automax-red">{truck.brand}</span>
              <span>•</span> {truck.category}
            </p>
          </div>
          <div className="text-left md:text-right border-t md:border-t-0 pt-4 md:pt-0 border-gray-100">
            {/* Price removed */}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">

            {/* Image - Optimized Height */}
            <div
              className="bg-white p-2 rounded-sm shadow-sm group" // Added group class for hover effect on buttons
            >
              <div className="relative aspect-[16/10] bg-gray-200 overflow-hidden rounded-sm">
                <img
                  key={activeImage} // Force re-render on change to animate if needed, or just ensures update
                  src={activeImage || truck.image || FALLBACK_IMAGE}
                  alt={truck.name}
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />

                {/* Navigation Buttons */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
                      aria-label="Previous Image"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
                      aria-label="Next Image"
                    >
                      <ChevronRight size={24} />
                    </button>
                    <div className="absolute bottom-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded-sm backdrop-blur-sm">
                      {allImages.indexOf(activeImage) + 1} / {allImages.length}
                    </div>
                  </>
                )}

                {!truck.available && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-none">
                    <span className="bg-red-600 text-white px-6 py-3 font-display font-bold uppercase tracking-widest text-2xl rotate-[-15deg] border-4 border-white shadow-xl">Sold Out</span>
                  </div>
                )}
              </div>
            </div>

            {/* Gallery Grid */}
            {allImages.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {allImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`aspect-[16/10] bg-gray-200 rounded-sm overflow-hidden cursor-pointer hover:opacity-90 transition-all ${activeImage === img ? 'ring-2 ring-automax-red ring-offset-1' : 'opacity-70 hover:opacity-100'}`}
                    onClick={() => setActiveImage(img)}
                  >
                    <img
                      src={img}
                      alt={`${truck.name} - view ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Specs Grid */}
            <div className="bg-white p-6 md:p-8 rounded-sm shadow-sm border border-gray-100">
              <h3 className="font-display text-xl md:text-2xl font-bold text-gray-900 uppercase mb-6 border-b pb-2 flex items-center gap-2">
                <Settings size={20} className="text-automax-red" /> Specifications
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6">
                {specs.map((spec, index) => (
                  <div key={index} className="space-y-1 p-2 hover:bg-gray-50 rounded-sm transition-colors">
                    <div className="text-gray-400 text-xs uppercase tracking-wider flex items-center gap-1">
                      {spec.icon} {spec.label}
                    </div>
                    <div className="font-bold text-gray-900 text-sm md:text-base">{spec.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white p-6 md:p-8 rounded-sm shadow-sm border border-gray-100">
              <h3 className="font-display text-xl md:text-2xl font-bold text-gray-900 uppercase mb-4 border-b pb-2 flex items-center gap-2">
                <Info size={20} className="text-automax-red" /> Description
              </h3>
              <div
                className="prose max-w-none text-gray-700 leading-relaxed text-sm md:text-base prose-headings:font-display prose-headings:uppercase prose-strong:text-automax-red prose-a:text-automax-red prose-ul:list-disc prose-ul:pl-4 prose-ol:list-decimal prose-ol:pl-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(truck.description || "No description available for this vehicle.") }}
              />
            </div>

            {/* Features */}
            <div className="bg-white p-6 md:p-8 rounded-sm shadow-sm border border-gray-100">
              <h3 className="font-display text-xl md:text-2xl font-bold text-gray-900 uppercase mb-6 border-b pb-2 flex items-center gap-2">
                <CheckCircle size={20} className="text-automax-red" /> Key Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {truck.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-gray-700 bg-gray-50 p-3 rounded-sm text-sm">
                    <CheckCircle size={16} className="text-automax-red mr-3 flex-shrink-0" />
                    <span className="font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Similar Vehicles */}
            {similarTrucks.length > 0 && (
              <div className="pt-8 border-t border-gray-200">
                <h3 className="font-display text-2xl font-bold text-gray-900 uppercase mb-6">Similar Vehicles</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {similarTrucks.map(sim => (
                    <Link key={sim.id} to={`/inventory/${sim.id}`} className="group bg-white rounded-sm shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="h-32 overflow-hidden relative">
                        <img
                          src={sim.image || FALLBACK_IMAGE}
                          alt={sim.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={handleImageError}
                        />
                        <div className="absolute bottom-0 left-0 bg-black/50 text-white text-xs px-2 py-1 font-bold">
                          {sim.category}
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="font-bold text-sm text-gray-900 line-clamp-1 group-hover:text-automax-red transition-colors">{sim.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{sim.maxSpeed}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Sticky on Desktop */}
          <div className="lg:col-span-1 space-y-6">
            <div className="lg:sticky lg:top-24 space-y-6">

              {/* Status Card */}
              <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 font-bold uppercase text-xs tracking-wider">Availability</span>
                  {truck.available ? (
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-sm text-xs font-bold uppercase flex items-center">
                      <CheckCircle size={14} className="mr-1" /> Available
                    </span>
                  ) : (
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-sm text-xs font-bold uppercase">
                      Sold Out
                    </span>
                  )}
                </div>
                <div className="flex items-center text-gray-600 text-sm mb-6">
                  <MapPin size={16} className="text-automax-red mr-2" />
                  <span>Located in <span className="font-bold text-gray-900">{truck.location}</span></span>
                </div>

                <div className="flex gap-2">
                  <button className="flex-1 bg-gray-100 text-gray-900 py-3 md:py-2 rounded-sm font-bold text-sm uppercase hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
                    <Share2 size={16} /> Share
                  </button>
                  <Link to="/financing" className="flex-1 bg-blue-50 text-blue-900 py-3 md:py-2 rounded-sm font-bold text-sm uppercase hover:bg-blue-100 transition-colors flex items-center justify-center gap-2">
                    Financing
                  </Link>
                </div>
              </div>

              {/* Inquiry Form */}
              <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100">
                <h3 className="font-display text-xl font-bold uppercase mb-2 text-gray-900">Interested?</h3>
                <p className="text-gray-500 text-sm mb-6">Send us a message and we'll get back to you with a quote.</p>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 p-4 rounded-sm text-center animate-pulse">
                    <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
                    <p className="font-bold text-green-700">Request Sent!</p>
                    <p className="text-sm text-gray-600 mt-1">Our sales team will contact you shortly.</p>
                    <button onClick={() => setSubmitted(false)} className="text-xs text-automax-red underline mt-4 hover:text-red-700">Send another</button>
                  </div>
                ) : (
                  <form onSubmit={handleInquirySubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Full Name"
                        required
                        value={inquiryForm.name}
                        onChange={e => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                        className="w-full bg-gray-50 border border-gray-300 text-gray-900 px-4 py-3 rounded-sm focus:border-automax-red outline-none placeholder-gray-500 text-sm transition-colors focus:bg-white"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="Email Address"
                        required
                        value={inquiryForm.email}
                        onChange={e => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                        className="w-full bg-gray-50 border border-gray-300 text-gray-900 px-4 py-3 rounded-sm focus:border-automax-red outline-none placeholder-gray-500 text-sm transition-colors focus:bg-white"
                      />
                    </div>
                    <div>
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        required
                        value={inquiryForm.phone}
                        onChange={e => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                        className="w-full bg-gray-50 border border-gray-300 text-gray-900 px-4 py-3 rounded-sm focus:border-automax-red outline-none placeholder-gray-500 text-sm transition-colors focus:bg-white"
                      />
                    </div>
                    <div>
                      <textarea
                        rows={3}
                        placeholder="Message"
                        value={inquiryForm.message}
                        onChange={e => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                        className="w-full bg-gray-50 border border-gray-300 text-gray-900 px-4 py-3 rounded-sm focus:border-automax-red outline-none placeholder-gray-500 text-sm transition-colors focus:bg-white resize-none"
                      ></textarea>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <button type="submit" disabled={isSending} className={`w-full bg-automax-red hover:bg-red-700 text-white font-bold py-3 rounded-sm uppercase tracking-wider transition-colors text-sm flex items-center justify-center gap-2 ${isSending ? 'opacity-70 cursor-not-allowed' : ''}`}>
                        {isSending ? 'Sending...' : <>Request Quote <ArrowRight size={16} /></>}
                      </button>
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-3 rounded-sm uppercase tracking-wider transition-colors text-sm flex items-center justify-center gap-2"
                      >
                        <MessageCircle size={18} /> Chat on WhatsApp
                      </a>
                    </div>
                  </form>
                )}
              </div>

              {/* Direct Contact */}
              <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100 space-y-4">
                <div className="flex items-center gap-4 border-b border-gray-50 pb-4">
                  <div className="bg-red-50 p-3 rounded-full text-automax-red">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold">Call Us 24/7</p>
                    <p className="font-bold text-gray-900">{settings.phoneNumber}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-red-50 p-3 rounded-full text-automax-red">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold">Email Sales</p>
                    <p className="font-bold text-gray-900 text-sm break-all">{settings.email}</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetails;
